import { useState, useEffect, useCallback } from 'react';
import { Send, Clock, Plus, RefreshCw, CheckCircle, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '../../../api/client';

interface TgAccount {
  id: number;
  label: string;
  chat_id: string;
  chat_type: string;
  chat_title: string | null;
  is_active: boolean;
  channels: string[];
  seller_ids: number[];
  quiet_hours: { from: string; to: string; timezone: string } | null;
  total_sent: number;
  total_failed: number;
  last_sent_at: string | null;
}

export function TelegramTab({ t }: { t: (k: string) => string }) {
  const [accounts, setAccounts] = useState<TgAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Form state
  const [form, setForm] = useState({
    label: '',
    bot_token: '',
    chat_id: '',
    channels: ['questions', 'messages', 'claims'] as string[],
    seller_ids: [] as number[],
    quiet_from: '',
    quiet_to: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<TgAccount[]>('/telegram/accounts');
    if (res.success && res.data) setAccounts(res.data as unknown as TgAccount[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    if (!form.label || !form.bot_token || !form.chat_id) return;
    setSaving(true);
    const body: Record<string, unknown> = {
      label: form.label,
      bot_token: form.bot_token,
      chat_id: form.chat_id,
      channels: form.channels,
      seller_ids: form.seller_ids,
    };
    if (form.quiet_from && form.quiet_to) {
      body.quiet_hours = { from: form.quiet_from, to: form.quiet_to, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    }

    const res = await api.post<any>('/telegram/accounts', body);
    setSaving(false);

    if (res.success) {
      showToast(t('settings.tgSaved'), true);
      setShowForm(false);
      setForm({ label: '', bot_token: '', chat_id: '', channels: ['questions', 'messages', 'claims'], seller_ids: [], quiet_from: '', quiet_to: '' });
      load();
    } else {
      showToast((res as any).error || 'Error', false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('settings.tgDeleteConfirm'))) return;
    await api.delete(`/telegram/accounts/${id}`);
    showToast(t('settings.tgDeleted'), true);
    load();
  };

  const handleTest = async (id: number) => {
    setTesting(id);
    const res = await api.post<any>(`/telegram/accounts/${id}/test`, {});
    setTesting(null);
    showToast(res.success ? t('settings.tgTestSuccess') : t('settings.tgTestError'), !!res.success);
  };

  const handleToggle = async (id: number, currentActive: boolean) => {
    await api.put(`/telegram/accounts/${id}`, { is_active: !currentActive });
    load();
  };

  const handleChannelToggle = async (id: number, currentChannels: string[], channel: string) => {
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    await api.put(`/telegram/accounts/${id}`, { channels: newChannels });
    load();
  };

  const channelColors: Record<string, string> = {
    questions: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    messages: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    claims: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const channelLabels: Record<string, string> = {
    questions: t('settings.tgQuestions'),
    messages: t('settings.tgMessages'),
    claims: t('settings.tgClaims'),
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8 text-[var(--color-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-sm font-medium shadow-lg ${
          toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.tgTitle')}</h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">{t('settings.tgDesc')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90"
          >
            <Plus size={14} /> {t('settings.tgAddAccount')}
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="border border-[var(--color-border)] rounded-lg p-4 space-y-4 bg-[var(--color-surface-hover)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">{t('settings.tgLabel')}</label>
              <input
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder={t('settings.tgLabelPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">{t('settings.tgChatId')}</label>
              <input
                value={form.chat_id}
                onChange={e => setForm(f => ({ ...f, chat_id: e.target.value }))}
                placeholder={t('settings.tgChatIdPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">{t('settings.tgBotToken')}</label>
            <input
              value={form.bot_token}
              onChange={e => setForm(f => ({ ...f, bot_token: e.target.value }))}
              placeholder={t('settings.tgBotTokenPlaceholder')}
              type="password"
              className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm font-mono"
            />
          </div>

          {/* Channels */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-2">{t('settings.tgChannels')}</label>
            <div className="flex gap-2">
              {(['questions', 'messages', 'claims'] as const).map(ch => (
                <button
                  key={ch}
                  onClick={() => setForm(f => ({
                    ...f,
                    channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch],
                  }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    form.channels.includes(ch)
                      ? channelColors[ch]
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {channelLabels[ch]}
                </button>
              ))}
            </div>
          </div>

          {/* Quiet Hours */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-2">{t('settings.tgQuietHours')}</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-text-muted)]">{t('settings.tgFrom')}</span>
              <input
                type="time"
                value={form.quiet_from}
                onChange={e => setForm(f => ({ ...f, quiet_from: e.target.value }))}
                className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-xs"
              />
              <span className="text-xs text-[var(--color-text-muted)]">{t('settings.tgTo')}</span>
              <input
                type="time"
                value={form.quiet_to}
                onChange={e => setForm(f => ({ ...f, quiet_to: e.target.value }))}
                className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-xs"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.label || !form.bot_token || !form.chat_id}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {t('common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Accounts list */}
      {accounts.length === 0 && !showForm ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <Send size={40} className="mx-auto mb-3 opacity-20" />
          <p>{t('settings.tgNoAccounts')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map(account => (
            <div key={account.id} className="border border-[var(--color-border)] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    account.is_active ? 'bg-blue-500/20' : 'bg-gray-500/20'
                  }`}>
                    <Send size={16} className={account.is_active ? 'text-blue-400' : 'text-gray-500'} />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-[var(--color-text)]">{account.label}</span>
                    <div className="flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
                      <span className="font-mono">{account.chat_id}</span>
                      {account.chat_title && <span>({account.chat_title})</span>}
                      <span className="capitalize">{account.chat_type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Stats */}
                  <span className="text-[10px] text-[var(--color-text-muted)]">
                    {account.total_sent} {t('settings.tgSent')}
                    {account.total_failed > 0 && <span className="text-red-400 ml-1">{account.total_failed} {t('settings.tgFailed')}</span>}
                  </span>

                  {/* Toggle */}
                  <button onClick={() => handleToggle(account.id, account.is_active)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                    {account.is_active ? <ToggleRight size={22} className="text-green-500" /> : <ToggleLeft size={22} />}
                  </button>

                  {/* Test */}
                  <button
                    onClick={() => handleTest(account.id)}
                    disabled={testing === account.id}
                    className="px-2 py-1 text-[10px] rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
                  >
                    {testing === account.id ? <RefreshCw size={10} className="animate-spin" /> : t('settings.tgTest')}
                  </button>

                  {/* Delete */}
                  <button onClick={() => handleDelete(account.id)}
                    className="p-1 rounded hover:bg-red-500/20 text-[var(--color-text-muted)] hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Channels toggles */}
              <div className="flex items-center gap-2">
                {(['questions', 'messages', 'claims'] as const).map(ch => (
                  <button
                    key={ch}
                    onClick={() => handleChannelToggle(account.id, account.channels, ch)}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${
                      account.channels.includes(ch)
                        ? channelColors[ch]
                        : 'border-[var(--color-border)] text-[var(--color-text-muted)] opacity-40'
                    }`}
                  >
                    {channelLabels[ch]}
                  </button>
                ))}

                {account.quiet_hours && (
                  <span className="text-[10px] text-[var(--color-text-muted)] ml-2 flex items-center gap-1">
                    <Clock size={10} /> {account.quiet_hours.from}-{account.quiet_hours.to}
                  </span>
                )}

                {account.last_sent_at && (
                  <span className="text-[10px] text-[var(--color-text-muted)] ml-auto">
                    {t('settings.tgLastSent')}: {new Date(account.last_sent_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
